---
title: "Google Maps API pricing per 1,000 places and 1,000 reviews"
description: "What 1,000 Google Maps places and 1,000 reviews cost on Google's Places API, four scraper APIs, and a no-code tool. Free to 1,000 searches, then $35 per 1,000."
date: "2026-09-17"
postType: "alternatives"
author: "Kevin Wang"
tags: ["google-maps", "places-api", "api", "pricing", "comparison"]
keyword: "google maps api pricing"
ogImage: "/og/google-maps-api-pricing.png"
---

export const placesPer1k = [
  { label: "AnyAPI", sku: "maps.search", items: 20 },
  { label: "SerpApi", value: 1.25 },
  { label: "Google Places API", value: 1.75 },
  { label: "Outscraper", value: 3.0 },
  { label: "Apify", value: 4.0 },
];

export const faqItems = [
  {
    q: "Does Google still give $200 of free Maps usage a month?",
    a: "No. On March 1, 2025 Google replaced the $200 monthly credit with free requests per price tier: 10,000 requests a month on Essentials, 5,000 on Pro, and 1,000 on Enterprise and Enterprise + Atmosphere. The free requests reset on the first of the month, Pacific time. A request that asks for a phone number or a rating is an Enterprise request, so it gets 1,000 free, not 10,000.",
  },
  {
    q: "Can I get more than five reviews per place from the Places API?",
    a: "No. The Place resource in Places API (New) says a maximum of 5 reviews can be returned, sorted by relevance, and there's no page token for reviews. If you need a place's full review history you need a scraper API or an extractor; every route on this page other than Google's returns 100 or more reviews for one place.",
  },
  {
    q: "Do I need a Google Cloud project to use a scraper API?",
    a: "No. The scraper APIs and the no-code extractor on this page read the public Google Maps site and sell you the result, so there's no Google Cloud project, no API key from Google, and no Google terms on how you cache or display the data. That last point is the reason to check each vendor's own terms and Google's before you build on them.",
  },
];

<Callout type="tip" title="Key findings">

- **Under 1,000 searches a month, use Google.** The [Places API](https://developers.google.com/maps/billing-and-pricing/pricing) gives you 1,000 free Enterprise requests a month, which is 20,000 places at no cost.
- **Past that, Google is the most expensive route.** A thousand listings with phone, website, and rating cost $1.75 from Google, $1.25 from [SerpApi](https://serpapi.com/pricing), $3.00 from [Outscraper](https://outscraper.com/pricing/), $4.00 from [Apify](https://apify.com/compass/crawler-google-places), and <Price sku="maps.search" items={20} /> from [AnyAPI](https://getanyapi.com/api/maps).
- **For reviews, Google isn't an option at any price.** The Places API returns at most 5 reviews per place. Every other route here returns 100 or more, and AnyAPI does 1,000 of them for <Price sku="maps.reviews" items={100} />.
- **If you want a spreadsheet and no code, use [GMPlus](https://gmplus.io/).** It exports 500 listings a day for free, with more fields per record than either API returns.

</Callout>

Google Maps has the business listing you want: name, address, phone, website, hours, rating, and the reviews behind the rating. You can buy that data three ways. Google sells it through the Places API and, since March 2025, prices each request by which fields you ask for. Scraper APIs sell the same listing read off the public map. No-code extractors sell it as a CSV.

I priced all three routes for the two jobs people actually have, a list of places matching a search and the reviews of one place, on each vendor's cheapest self-serve tier. Then I sent the same dentist search and the same 100-review pull through the APIs that would take it, so the prices below sit next to what actually came back.

The short version: Google is free for a small app and the priciest option once you outgrow the free requests, it caps reviews at five per place, and the cheapest API route is about 20 times cheaper per place. Which one you pick comes down to volume and whether you need reviews.

## The best ways to get Google Maps data

- [Google Places API](#best-way-to-get-google-maps-data-for-under-1000-searches-a-month) for under 1,000 searches a month
- [AnyAPI](#best-way-to-get-google-maps-data-for-the-lowest-price-per-place-and-per-review) for the lowest price per place and per review
- [SerpApi](#best-way-to-get-google-maps-data-for-one-vendor-across-every-google-surface) for one vendor across every Google surface
- [Outscraper](#best-way-to-get-google-maps-data-for-per-place-billing-on-sparse-searches) for per-place billing on sparse searches
- [Apify](#best-way-to-get-google-maps-data-for-reviews-if-youre-already-on-apify) for reviews if you're already on Apify
- [GMPlus](#best-way-to-get-google-maps-data-for-a-spreadsheet-with-no-code) for a spreadsheet with no code

## What makes a good Google Maps API?

I'm assuming you need business listings in bulk, for a lead list, a directory, a market map, or a review monitor, and that you'd rather not run a headless browser against Google yourself. With that reader in mind, here's what I compared them on:

- **Price per 1,000 places:** the same job on every route, 1,000 places with name, address, phone, website, and rating, which is 50 pages of 20.
- **Price per 1,000 reviews:** the reviews of one place, and how many you can actually get.
- **Fields per record:** whether phone, website, hours, and rating come back, and what it costs to ask for them.
- **Free tier:** what you get before you pay, listed separately, because a free tier runs out and a price doesn't.
- **The call and the response:** what you send, what comes back, and how long it took.

<Callout type="note" title="How this was tested, and its limits">

What I didn't grade: accuracy, freshness, and uptime. Field coverage also differs by route.

One search and one reviews call through AnyAPI on September 17, 2026 is a shape and price check, not a benchmark. The 42-second reviews call is what happened that day.

Google's API wasn't called for this page; its numbers are from its pricing list and field documentation, both checked the same day.

Every bill assumes 20 places per page. A search that returns 4 places costs the per-search vendors the same as one that returns 20, and the per-place vendors a fifth.

Google's prices are the 0 to 100,000 requests a month tier. Above that the Enterprise Text Search rate drops to $28.00 per 1,000, and enterprise contracts aren't included.

SerpApi's per-search price is the $25 Starter plan. Apify's per-event prices are the free plan's, and the Apify figure excludes platform usage and any filter add-ons.

GMPlus's credit cost per review isn't published, so its reviews column states the free tier only.

</Callout>

## The best ways to get Google Maps data at a glance

| Route | Best for | 1,000 places | 1,000 reviews | Free |
| --- | --- | --- | --- | --- |
| Google Places API | Under 1,000 searches a month | $1.75 | Not possible, 5 per place | 1,000 Enterprise requests a month |
| AnyAPI | Lowest price per place and per review | <Price sku="maps.search" items={20} /> | <Price sku="maps.reviews" items={100} /> | Free credit at signup |
| SerpApi | One vendor for every Google surface | $1.25 | $1.28 | 250 searches a month |
| Outscraper | Per-place billing on sparse searches | $3.00 | $3.00 | 500 places and 500 reviews every 30 days |
| Apify | Reviews, if you're already on Apify | $4.00 | $0.50 | Free plan rates above, $0.002 per place on Silver |
| GMPlus | A spreadsheet with no code | $0.00 | Free tier | 500 records a day |

<Chart caption="Cost of 1,000 Google Maps listings with phone, website, and rating on each route's cheapest self-serve tier, prices checked 2026-09-17. Google is Text Search Enterprise at $35 per 1,000 pages of 20; SerpApi is the $25 Starter plan; Apify is the free plan's pay-per-event rate. GMPlus isn't plotted because 1,000 records fit inside two days of its free credits.">
  <BarChart data={placesPer1k} showValues valueSuffix="" baseline={0} height={220} format={(n) => `$${n.toFixed(n < 1 ? 4 : 2)}`} ariaLabel="Cost of 1,000 places" />
</Chart>

## Best way to get Google Maps data for under 1,000 searches a month

### Google Places API

**Google Places API pros:**

- 1,000 free Enterprise requests a month, which is 20,000 places at no cost
- Text Search with place IDs only is free at any volume
- Google's uptime, Google's terms, and a field for everything the map shows

**Google Places API cons:**

- $35.00 per 1,000 searches once you're past the free requests, the highest per-place price here
- A maximum of 5 reviews per place, sorted by relevance, with no page token
- Asking for a phone number or a rating cuts your free requests from 10,000 to 1,000

A Places API (New) request is billed by price tier, and the tier is decided by the fields in your field mask, not the endpoint you called. Ask a Text Search for place IDs only and it's an Essentials request. Add the display name and address and it's Pro. Add the phone number, website, hours, or rating and it's Enterprise. Add reviews, or any of the "serves breakfast" style attributes, and it's Enterprise + Atmosphere.

Here's the price per 1,000 requests at up to 100,000 a month, from Google's pricing list. Each tier gets its own free requests every month: 10,000 requests on Essentials, 5,000 on Pro, and 1,000 on Enterprise and Enterprise + Atmosphere.

| Request | Essentials | Pro | Enterprise | Enterprise + Atmosphere |
| --- | --- | --- | --- | --- |
| Text Search | IDs only, free | $32.00 | $35.00 | $40.00 |
| Nearby Search | not offered | $32.00 | $35.00 | $40.00 |
| Place Details | $5.00 | $17.00 | $20.00 | $25.00 |

**The fields a lead list needs put every search in the Enterprise tier**, so you get 1,000 free searches, and each one past that is $0.035. Reviews are the field Google prices highest and returns least of: a Place Details request that includes `reviews` is Enterprise + Atmosphere at $25.00 per 1,000, and the Place resource docs are explicit that "A maximum of 5 reviews can be returned." Five is all you get.

Here's the request for a dentist search in Austin with the fields a lead list needs. The field mask is what makes it an Enterprise request:

```bash
curl -sS -X POST https://places.googleapis.com/v1/places:searchText \
  -H "Content-Type: application/json" \
  -H "X-Goog-Api-Key: $GOOGLE_MAPS_KEY" \
  -H "X-Goog-FieldMask: places.id,places.displayName,places.formattedAddress,places.nationalPhoneNumber,places.websiteUri,places.rating,places.userRatingCount" \
  -d '{"textQuery":"dentist in Austin, TX","pageSize":20}'
```

That's $0.035, or one of your 1,000 free Enterprise requests. Drop `nationalPhoneNumber`, `websiteUri`, `rating`, and `userRatingCount` from the mask and the same call is a Pro request at $0.032 with 5,000 free a month. The price barely moves; the free requests go up fivefold.

Volume discounts start at 100,000 requests a month, where Enterprise Text Search falls to $28.00 per 1,000, then $21.00 above 500,000 and $10.50 above a million. Almost nobody reading this is above 100,000, so the rest of this page uses the first tier. If you're under 1,000 searches a month, or building an app that shows the data to users on a map, this is the answer and you can stop reading.

**Google Places API pricing:** Text Search $32.00 (Pro), $35.00 (Enterprise), $40.00 (Enterprise + Atmosphere) per 1,000; Place Details $5.00 to $25.00 per 1,000; 10,000, 5,000, or 1,000 free requests a month by tier

## Best way to get Google Maps data for the lowest price per place and per review

### AnyAPI

**AnyAPI pros:**

- <Price sku="maps.search" per="call" /> per search of 20 places and <Price sku="maps.reviews" per="call" /> per 100 reviews, the lowest per-place and per-review prices here
- Every field comes back on every call, so there's no field mask and no tier to think about
- Reviews come 100 to a request, sorted as you ask, with Google's own place IDs

**AnyAPI cons:**

- The 100-review call took 42 seconds, which is fine for a nightly job and too slow for a page someone is waiting on
- No price level or email in the search response

You put money in a wallet and every search takes <Price sku="maps.search" per="call" /> out of it. There's no plan to size and no credits that expire, and the price is on the endpoint page before you call it. **At 1,000 places the bill is <Price sku="maps.search" items={20} />, about 20 times under Google's rate and 14 times under SerpApi's.**

The same dentist search, with no field mask because every field comes back on every call:

<CodeTabs labels={["cURL", "Python", "JavaScript"]}>

```bash
curl -sS -X POST https://api.getanyapi.com/v1/run/maps.search \
  -H "Authorization: Bearer $ANYAPI_KEY" \
  -H "Content-Type: application/json" \
  -d '{"query":"dentist","location":"Austin, TX"}'
```

```python
import os, requests

r = requests.post(
    "https://api.getanyapi.com/v1/run/maps.search",
    headers={"Authorization": f"Bearer {os.environ['ANYAPI_KEY']}"},
    json={"query": "dentist", "location": "Austin, TX"},
    timeout=60,
)
body = r.json()
for place in body["output"]["data"]["items"]:
    print(place["name"], place["phone"], place["rating"], place["reviewCount"])
print(body["costUsd"])
```

```javascript
const r = await fetch("https://api.getanyapi.com/v1/run/maps.search", {
  method: "POST",
  headers: {
    Authorization: `Bearer ${process.env.ANYAPI_KEY}`,
    "Content-Type": "application/json",
  },
  body: JSON.stringify({ query: "dentist", location: "Austin, TX" }),
});
const body = await r.json();
for (const place of body.output.data.items) {
  console.log(place.name, place.phone, place.rating, place.reviewCount);
}
console.log(body.costUsd);
```

</CodeTabs>

On September 17, twenty places came back in 2.2 seconds, billed at $0.00175. The first one, trimmed to the fields that matter: {/* receipt 2026-09-17 */}

```json
{
  "costUsd": 0.00175,
  "items": 20,
  "output": {
    "found": true,
    "data": {
      "items": [
        {
          "name": "ATX Family Dental",
          "category": "Dentist",
          "address": "ATX Family Dental, 1700 S 1st St, Austin, TX 78704",
          "phone": "+15127173147",
          "website": "https://www.atxfamilydental.com/",
          "rating": 4.9,
          "reviewCount": 1245,
          "placeId": "ChIJb5gBC-u0RIYRHkntaUcdJGs"
        }
      ]
    }
  }
}
```

Each record also carries the Google CID, coordinates, the Maps URL, a photo, the opening hours for each day of the week, and whether the business is permanently closed. What it doesn't carry is price level or an email address.

The `placeId` is Google's own, so it works on the Places API too. Feed it to the reviews endpoint and you get 100 reviews in one call:

```bash
curl -sS -X POST https://api.getanyapi.com/v1/run/maps.reviews \
  -H "Authorization: Bearer $ANYAPI_KEY" \
  -H "Content-Type: application/json" \
  -d '{"placeId":"ChIJb5gBC-u0RIYRHkntaUcdJGs"}'
```

```json
{
  "costUsd": 0.0035,
  "items": 100,
  "output": {
    "found": true,
    "data": {
      "items": [
        {
          "author": "Lynh Warnken",
          "rating": 5,
          "publishedAgo": "8 hours ago",
          "createdUtc": 1789588483,
          "text": "We have been taking our 3-year-old son to ATX Family Dental for the past year...",
          "likes": 0,
          "isLocalGuide": false,
          "reviewerReviewsCount": 4,
          "ownerResponse": "",
          "reviewId": "Ci9DQUlRQUNvZENodHljRjlvT2t0Nk4zZHlNalF5ZUhoQ2FsRkRWbEo2VlhabFJGRRAB"
        }
      ]
    }
  }
}
```

That was 42 seconds for 100 reviews at $0.0035 on September 17. Google would have given you 5 of them for $0.025. {/* receipt 2026-09-17 */}

**AnyAPI pricing:** <Price sku="maps.search" per="call" /> per search of 20 places (<Price sku="maps.search" /> per 1,000 searches), <Price sku="maps.reviews" per="call" /> per 100 reviews (<Price sku="maps.reviews" /> per 1,000 reviews), prepaid wallet, free credit at signup

## Best way to get Google Maps data for one vendor across every Google surface

### SerpApi

**SerpApi pros:**

- One account and one response shape for Maps, Google Search, Shopping, News, and the rest
- A support desk behind the API
- 250 free searches a month to build against

**SerpApi cons:**

- $0.025 a search on the $25 Starter plan, which is 14 times AnyAPI and only 40% under Google
- Reviews are paged as searches: 8 on the first page, then up to 20 per page, so 1,000 reviews is about $1.28

If Maps is one of several Google surfaces in the same program, SerpApi is the tidy choice: the Google Maps engine and the Google Maps Reviews engine sit next to the web search engine you'd use anyway, and everything comes back in the same envelope. You pay for that convenience per search, and the per-search price only gets competitive on the big plans. **On the $725 plan the same search is $0.00725, which puts 1,000 places at $0.36.**

Like AnyAPI, SerpApi bills per search rather than per place, so a page with 20 results is cheap and a page with two results isn't.

**SerpApi pricing:** $25 a month for 1,000 searches ($0.025 each) on Starter, down to $0.00725 a search on the $725 plan; 250 free searches a month

## Best way to get Google Maps data for per-place billing on sparse searches

### Outscraper

**Outscraper pros:**

- Billed per place and per review, so a search that returns four places costs a fifth of one that returns 20
- 500 places and 500 reviews free every 30 days
- A reviews scraper that takes a place and returns as many reviews as you ask for

**Outscraper cons:**

- $0.003 per place and per review, the highest paid rate on this page for both jobs

Outscraper and Apify charge for what comes back, not for what you asked. That matters when you're searching small towns or narrow categories, where most pages come back nearly empty and the per-search vendors charge you full price for two results. On a page full of results, though, Outscraper is the most expensive route here: $3.00 for 1,000 places and $3.00 for 1,000 reviews, against <Price sku="maps.search" items={20} /> and <Price sku="maps.reviews" items={100} /> on AnyAPI.

**Outscraper pricing:** $0.003 per place, $0.003 per review; 500 of each free every 30 days

## Best way to get Google Maps data for reviews if you're already on Apify

### Apify

**Apify pros:**

- $0.0005 per review on the free plan, the second-cheapest reviews price here
- Per-place billing, and the Silver plan halves the place price to $0.002
- Scheduling, storage, and a dataset export without writing a job runner

**Apify cons:**

- $0.004 per place on the free plan, the most expensive listing price here
- Each filter you apply and each extra detail block is billed on top, so the bill grows with the options you tick
- Platform usage is billed separately from the per-event prices

Apify's Google Maps Scraper is an actor you rent by the event: one price per place, another per review, and another for each add-on. If you already run scrapes on Apify and want reviews, it's a fair route at $0.50 for 1,000 of them, plus $0.004 for the place itself. For places alone it's the dearest option on the free plan, and a large crawl on the Silver plan, at $0.002 a place, lands between SerpApi and Outscraper.

**Apify pricing:** $0.004 per place and $0.0005 per review on the free plan, $0.002 per place on Silver, filters and detail blocks extra, platform usage not included

## Best way to get Google Maps data for a spreadsheet with no code

### GMPlus

**GMPlus pros:**

- 500 credits a day free, and a standard record is 1 credit
- 27 fields per record, including opening hours, claimed status, price level, and the Plus Code, more than either API returns in one call
- A web app with no extension and no Google key: type the search, choose up to 10 areas, download CSV, XLSX, or JSON

**GMPlus cons:**

- Email extraction costs 5 credits a record instead of 1
- The credit cost per review isn't published, so I can't price 1,000 reviews on it

Most Google Maps data requests don't come from a developer. They come from someone who needs 300 roofing companies in Denver in a spreadsheet by Friday, and for that person an API key is a detour. GMPlus is the route for them, and its guides on [generating leads from Google Maps](https://gmplus.io/guide/how-to-generate-leads-from-google-maps) and [scraping Google Maps reviews](https://gmplus.io/google-map-reviews-scraper) are the walkthroughs. **If the list is going to be opened in a spreadsheet, 500 records a day is more than most lead lists need.**

The handover point is when the spreadsheet stops being the deliverable. Once someone wants the list refreshed every night, joined to a CRM, or fed to an agent, the same search moves to an API, and the place IDs in the GMPlus export are the same place IDs the APIs take.

**GMPlus pricing:** free, 500 credits a day; 1 credit per record, 5 with email extraction

## Which route should you take?

- Under 1,000 searches a month, or an app that shows the data to users on a map: Google's Places API, free, with Google's terms.
- A recurring list of places for a program, past 1,000 searches a month: AnyAPI at <Price sku="maps.search" per="call" /> a search.
- Reviews, at any volume: not Google. AnyAPI for the lowest price, Apify if you're already on Apify.
- A list in a spreadsheet, no code: GMPlus's free 500 records a day.
- One vendor for Maps plus every other Google surface, with a support desk: SerpApi.
- Sparse searches where most pages come back nearly empty: Outscraper or Apify, since they bill per place.

<FAQ items={faqItems} />

If Google web search is part of the same program, the [cheapest SERP API comparison](/blog/cheapest-serp-api) prices that at three volumes. The [Google Maps API page](/api/maps) has the live request format and the current price of each Maps endpoint, including nearby search and contact enrichment.

<CTA to="/api/maps" note="Google Maps search and reviews as clean JSON, billed per request from a prepaid wallet, no plan and nothing that expires. New accounts get free credit, no card required.">
  Try the Google Maps API
</CTA>

Use data responsibly and follow AnyAPI's [Acceptable Use Policy](/acceptable-use).
